import { expand } from './toggle';
import type { HierarchyPointNode } from 'd3';
import { splitName, visit } from './utils';
import type { TreeNode } from '../types';

// function to create the path to nodes for search
function getNodePath(node: HierarchyPointNode<TreeNode>, path) {
    const p = path || [],
        parent = node.parent;
    if (parent) {
        const children = parent.children || parent.data._children;
        p.unshift(children.indexOf(node));
        return getNodePath(parent, p);
    } else {
        return p;
    }
}

// function to set up the search
function createSearchList(root: HierarchyPointNode<TreeNode>): string[] {
    const searchList = [];
    visit(
        root,
        // creates the search list with paths to the nodes
        (current: HierarchyPointNode<TreeNode>) => {
            if (current) {
                searchList.push(current.data.name + '|' + getNodePath(current, []).join('-'));
            }
        },
        (current: HierarchyPointNode<TreeNode>) => {
            return current.children || current.data._children;
        }
    );
    // removes name of floorplan from search list
    searchList.splice(searchList.indexOf(root.data.name + '|'), 1).sort();
    return searchList;
}

function getDOMElement(selector: string): HTMLElement {
    return document.querySelector(selector);
}

function showDOMElement(element: HTMLElement): void {
    element.style.display = 'block';
}

function hideDOMElement(element: HTMLElement): void {
    element.style.display = 'none';
}

function removeDOMElements(container: HTMLElement): void {
    while (container.firstChild) {
        container.removeChild(container.firstChild);
    }
    hideDOMElement(container);
}

function createDOMElements(content: string, container: HTMLElement): void {
    const listElement = document.createElement('li');
    listElement.setAttribute('data-path', content.split('|')[1]);
    listElement.textContent = splitName(content.split('|')[0]);
    container.appendChild(listElement);
    showDOMElement(container);
}

function searchInputEventHandler(
    items: string[],
    inputElement: HTMLInputElement,
    icon: HTMLElement,
    container: HTMLElement
) {
    const searchValue = inputElement.value.toUpperCase().trim();
    if (searchValue === null || searchValue === '') {
        removeDOMElements(container);
        hideDOMElement(icon);
    } else {
        for (const item of items) {
            const listValue = item.split('|')[0].toUpperCase();
            if (listValue.indexOf(searchValue) >= 0 || listValue.replace(/\s/g, '').indexOf(searchValue) >= 0) {
                createDOMElements(item, container);
            }
            showDOMElement(icon);
        }
    }
}

export function expandNodePath(path: string[], rootNode: HierarchyPointNode<TreeNode>): HierarchyPointNode<TreeNode> {
    let node = rootNode; //start with root
    for (const element of path) {
        if (node.data._children) {
            expand(node);
        }
        node = node.children[element];
    }
    return node;
}

export function setupInBuiltSearch(treeData: HierarchyPointNode<TreeNode>): void {
    const searchInputElement = getDOMElement('#search-input') as HTMLInputElement;
    const searchResultContainer = getDOMElement('#search-result');
    const searchContainer = getDOMElement('#search-container');
    const searchIcon = getDOMElement('#search-icon');
    const searchCloseIcon = getDOMElement('#search-close-icon');
    if (!searchContainer) {
        return;
    } else {
        searchCloseIcon.addEventListener('click', () => {
            searchInputElement.value = null;
            removeDOMElements(searchResultContainer);
            hideDOMElement(searchCloseIcon);
        });
        searchContainer.addEventListener('focusin', () => {
            hideDOMElement(searchIcon);
        });
        searchContainer.addEventListener('focusout', () => {
            showDOMElement(searchIcon);
        });
        searchInputElement.addEventListener('input', () => {
            removeDOMElements(searchResultContainer);
            searchInputEventHandler(
                createSearchList(treeData),
                searchInputElement,
                searchCloseIcon,
                searchResultContainer
            );
        });
    }
}
